import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Send, Users, Search, X, FlaskConical, ImagePlus, FileText } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const TYPES = [
  ["all", "All F.E.R.Ms"],
  ["state", "By state"],
  ["electorate", "By electorate"],
  ["members", "Individuals"],
];
const MERGE = ["first_name", "name", "division", "state", "member_name", "member_party"];

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[#E2705A] border-[#E2705A] text-white" : "border-[#2C3E55] text-[#F8FAFC]/80 hover:border-[#8FA58A]"}`}>
    {children}
  </button>
);

const Picker = ({ items, selected, onToggle, placeholder, testId, render }) => {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (s ? items.filter((i) => i.search.includes(s)) : items).slice(0, 40);
  }, [items, q]);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-selected`}>
        {selected.map((id) => {
          const it = items.find((i) => i.id === id);
          return (
            <span key={id} className="inline-flex items-center gap-1 pl-3 pr-1 h-7 rounded-full bg-[#E2705A]/20 text-[#F8FAFC] text-xs">
              {it ? it.label : id}
              <button type="button" onClick={() => onToggle(id)} aria-label="Remove" className="w-5 h-5 rounded-full hover:bg-[#E2705A]/40 inline-flex items-center justify-center"><X className="w-3 h-3" /></button>
            </span>
          );
        })}
        {selected.length === 0 && <span className="text-xs text-[#F8FAFC]/40">Nothing selected yet</span>}
      </div>
      <div className="flex items-center gap-2 bg-[#121E2B] border border-[#2C3E55] rounded-lg px-3 h-10">
        <Search className="w-4 h-4 text-[#8FA58A]" />
        <input data-testid={`${testId}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="bg-transparent outline-none text-sm flex-1 text-[#F8FAFC]" />
      </div>
      <div className="max-h-48 overflow-y-auto rounded-lg border border-[#2C3E55] divide-y divide-[#2C3E55]/60" data-testid={`${testId}-list`}>
        {shown.map((it) => (
          <button key={it.id} type="button" onClick={() => onToggle(it.id)} data-testid={`${testId}-option-${it.id}`} className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#121E2B] ${selected.includes(it.id) ? "text-[#E2705A]" : "text-[#F8FAFC]/85"}`}>
            {render(it)}
            {selected.includes(it.id) && <span className="text-[10px] uppercase tracking-wider">selected</span>}
          </button>
        ))}
        {shown.length === 0 && <p className="px-3 py-3 text-xs text-[#F8FAFC]/40">No matches</p>}
      </div>
    </div>
  );
};

export const BroadcastComposer = () => {
  const qc = useQueryClient();
  const [type, setType] = useState("all");
  const [states, setStates] = useState([]);
  const [electorates, setElectorates] = useState([]);
  const [memberIds, setMemberIds] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [images, setImages] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const audience = { type, states, electorates, member_ids: memberIds };
  const { data: drafts = [] } = useQuery({ queryKey: ["broadcast-drafts"], queryFn: () => api.get("/admin/broadcast/drafts").then((r) => r.data) });
  const { data: elecs = [] } = useQuery({ queryKey: ["electorates"], queryFn: () => api.get("/electorates").then((r) => r.data) });
  const { data: members = [] } = useQuery({ queryKey: ["admin-members"], queryFn: () => api.get("/admin/members").then((r) => r.data) });
  const { data: preview } = useQuery({
    queryKey: ["broadcast-preview", audience],
    queryFn: () => api.post("/admin/broadcast/preview", audience).then((r) => r.data),
  });

  const elecItems = useMemo(() => elecs.map((e) => ({ id: e.name, label: `${e.name} (${e.state})`, meta: `${e.count} F.E.R.M${e.count === 1 ? "" : "s"}`, search: `${e.name} ${e.state}`.toLowerCase() })), [elecs]);
  const memberItems = useMemo(() => members.map((m) => ({ id: m.id, label: m.name, meta: `${m.email} · ${m.electorate}`, search: `${m.name} ${m.email} ${m.electorate}`.toLowerCase() })), [members]);
  const toggle = (set) => (id) => set((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const loadDraft = (d) => { setSubject(d.subject); setMessage(d.message); setImages(d.images); toast.success(`Loaded "${d.title}"`); };
  const setCaption = (i, caption) => setImages((cur) => cur.map((img, idx) => (idx === i ? { ...img, caption } : img)));
  const upload = async (files) => {
    const room = 6 - images.length;
    if (room <= 0) return toast.error("Maximum 6 pictures per email");
    setUploading(true);
    try {
      for (const f of Array.from(files).slice(0, room)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/broadcast/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((cur) => [...cur, { url: data.url, caption: "" }]);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  const send = async (testOnly) => {
    if (!subject.trim() || !message.trim()) return toast.error("Subject and message are required");
    if (!testOnly && !preview?.count) return toast.error("No recipients match that audience");
    if (!testOnly && !window.confirm(`Send "${subject}" to ${preview.count} F.E.R.M${preview.count === 1 ? "" : "s"} (${preview.label})?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", { subject, message, images, audience, test_only: testOnly, test_to: testOnly && testTo.trim() ? testTo.trim() : null });
      toast.success(testOnly ? `Test sent to ${data.to}` : `Sending to ${data.queued} member${data.queued === 1 ? "" : "s"} — check the log below`);
      if (!testOnly) { setSubject(""); setMessage(""); setImages([]); }
      setTimeout(() => { qc.invalidateQueries({ queryKey: ["admin-emails"] }); qc.invalidateQueries({ queryKey: ["admin-broadcasts"] }); }, 3000);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-broadcast" className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] p-5 space-y-5">
      <div>
        <h3 className="font-type font-bold text-lg">Send an email to F.E.R.Ms</h3>
        <p className="text-xs text-[#F8FAFC]/50 mt-1">Choose who gets it, write your message, and send. Each member receives their own copy in the site's email style.</p>
      </div>

      {drafts.length > 0 && (
        <div className="space-y-2" data-testid="broadcast-drafts">
          <Label className="font-type text-[11px] uppercase tracking-wider text-[#8FA58A]">Ready-made emails</Label>
          <div className="flex flex-wrap gap-2">
            {drafts.map((d) => (
              <button key={d.key} type="button" onClick={() => loadDraft(d)} data-testid={`broadcast-draft-${d.key}`} className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-[#8FA58A]/15 border border-[#8FA58A]/40 hover:bg-[#8FA58A]/30 text-xs font-semibold text-[#F8FAFC]">
                <FileText className="w-4 h-4 text-[#8FA58A]" /> {d.title} <span className="text-[#F8FAFC]/50 font-normal">· {d.images.length} pictures</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <Label className="font-type text-[11px] uppercase tracking-wider text-[#8FA58A]">Who</Label>
        <div className="flex flex-wrap gap-2" data-testid="broadcast-audience-type">
          {TYPES.map(([v, l]) => <Chip key={v} active={type === v} onClick={() => setType(v)} testId={`broadcast-type-${v}`}>{l}</Chip>)}
        </div>
        {type === "state" && (
          <div className="flex flex-wrap gap-2" data-testid="broadcast-states">
            {STATES.map((s) => <Chip key={s} active={states.includes(s)} onClick={() => toggle(setStates)(s)} testId={`broadcast-state-${s}`}>{s}</Chip>)}
          </div>
        )}
        {type === "electorate" && (
          <Picker items={elecItems} selected={electorates} onToggle={toggle(setElectorates)} placeholder="Search electorates…" testId="broadcast-electorates" render={(it) => <span>{it.label} <span className="text-xs text-[#F8FAFC]/40 ml-1">{it.meta}</span></span>} />
        )}
        {type === "members" && (
          <Picker items={memberItems} selected={memberIds} onToggle={toggle(setMemberIds)} placeholder="Search by name, email or electorate…" testId="broadcast-members" render={(it) => <span>{it.label} <span className="text-xs text-[#F8FAFC]/40 ml-1">{it.meta}</span></span>} />
        )}
        <p data-testid="broadcast-recipient-count" className="inline-flex items-center gap-2 text-sm text-[#F8FAFC]/85 bg-[#121E2B] rounded-full px-3 h-8">
          <Users className="w-4 h-4 text-[#8FA58A]" />
          <strong className="text-[#E2705A]">{preview?.count ?? "–"}</strong> recipient{preview?.count === 1 ? "" : "s"} · {preview?.label || ""}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label className="font-type text-[11px] uppercase tracking-wider text-[#8FA58A]">Subject</Label>
        <Input data-testid="broadcast-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. {{division}} — this fortnight's call" className="h-10 bg-[#121E2B] border-[#2C3E55]" />
      </div>
      <div className="space-y-1.5">
        <Label className="font-type text-[11px] uppercase tracking-wider text-[#8FA58A]">Message</Label>
        <Textarea data-testid="broadcast-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={9} placeholder={"G'day {{first_name}},\n\nBlank lines make new paragraphs. Links are clickable automatically."} className="bg-[#121E2B] border-[#2C3E55] text-sm" />
        <p className="text-[11px] text-[#F8FAFC]/50">
          Merge fields: {MERGE.map((m) => <code key={m} className="mx-0.5 px-1 rounded bg-[#121E2B] text-[#8FA58A]">{`{{${m}}}`}</code>)}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label className="font-type text-[11px] uppercase tracking-wider text-[#8FA58A]">Pictures (optional, up to 6 — shown under your message)</Label>
        {images.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="broadcast-images">
            {images.map((img, i) => (
              <div key={img.url} className="flex gap-3 rounded-xl bg-[#121E2B] border border-[#2C3E55] p-2" data-testid={`broadcast-image-${i}`}>
                <img src={img.url} alt="" className="w-16 h-24 object-cover rounded-lg bg-black/30" />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <Input data-testid={`broadcast-image-caption-${i}`} value={img.caption} onChange={(e) => setCaption(i, e.target.value)} placeholder="Caption (optional)" className="h-8 bg-[#1B2A41] border-[#2C3E55] text-xs" />
                  <button type="button" onClick={() => setImages((cur) => cur.filter((_, idx) => idx !== i))} data-testid={`broadcast-image-remove-${i}`} className="inline-flex items-center gap-1 text-[11px] text-[#F8FAFC]/60 hover:text-[#E2705A]"><X className="w-3 h-3" /> Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <label data-testid="broadcast-image-upload" className={`inline-flex items-center gap-2 h-9 px-4 rounded-full border border-dashed border-[#2C3E55] hover:border-[#8FA58A] text-xs cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4 text-[#8FA58A]" />} Add pictures
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => send(false)} disabled={busy} data-testid="broadcast-send" className="h-10 inline-flex items-center gap-2 px-5 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 font-type font-bold uppercase tracking-wider text-xs">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to {preview?.count ?? 0}
        </button>
        <div className="flex items-center gap-2">
          <Input data-testid="broadcast-test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Test inbox (defaults to notify address)" className="h-10 w-64 bg-[#121E2B] border-[#2C3E55] text-sm" />
          <button type="button" onClick={() => send(true)} disabled={busy} data-testid="broadcast-send-test" className="h-10 inline-flex items-center gap-2 px-4 rounded-full border border-[#2C3E55] hover:border-[#8FA58A] disabled:opacity-60 font-type font-bold uppercase tracking-wider text-xs whitespace-nowrap">
            <FlaskConical className="w-4 h-4" /> Send a test first
          </button>
        </div>
      </div>
    </div>
  );
};
